import * as React from "react"
import { toast } from "react-toastify"
import { APIError } from "../../api/ObservableFromFetch"
import { AppPageWrapper } from "../NavMenu/AppPageWrapper"
import { Button, Col, Container, Form, Modal, Spinner, Table } from "react-bootstrap"
import TablePagination from "../TablePagination"
import { FaSync } from "react-icons/fa"
import downloadCsv from "../../assets/images/download-csv.png"
import { Subscription } from "rxjs"
import FromToDates from "./FromToDates"
import FormatDate from "../../formatters/FormatDate"
import { PositiveReportExport, PositiveReportLoad, PositiveReportRow } from "../../api/Reports/WorkforceReport"
import { GenerateMROReport } from "../../api/OrderResults/GenerateCCFImage"
import FormatTimestampFileName from "../../formatters/FormatTimestampFileName"
import FileSaver from "file-saver"
import printPdf from "../../utils/printPdf"
import ImageMRO from "../../assets/images/MRO.png"
import GridResponse from "../../api/GridResponse"
import AutoFilterCompany from "../../api/Companies/CompanyModel/AutoFilterCompany"
import { GetAllAccountsByNameObservable } from "../../api/Companies/GetAccountsByName"
import { AccountType } from "../../config/Constants"
import DualListBox from "../Administration/DualListBox"
import ActionButton from "../ActionButton"

interface PositiveReportProps {
}

interface PositiveReportState {
  isLoading: boolean
  pageNumber: number
  ranges: string[]
  selectedRange: string
  selectedCompanies: AutoFilterCompany[]
  companies: AutoFilterCompany[]
  companiesLoading: boolean
  loadingCSV: boolean
  from?: Date
  to?: Date
  data?: GridResponse<PositiveReportRow>
}

const PageSize = 20

export default class PositiveReport extends React.PureComponent<PositiveReportProps, PositiveReportState> {
  ranges = ["Collected", "Reported"]

  state: PositiveReportState = {
    isLoading: false,
    loadingCSV: false,
    pageNumber: 1,
    ranges: this.ranges,
    selectedRange: this.ranges[0],
    selectedCompanies: [],
    companies: [],
    companiesLoading: false
  }

  subscriptions = new Subscription()
  exportSubscription?: Subscription
  loadImageSubscription?: Subscription
  loadReportSubscription?: Subscription

  componentDidMount() {
    this.setState({ companiesLoading: true })
    this.subscriptions.add(
      GetAllAccountsByNameObservable("", undefined, AccountType.Company).subscribe(
        (results) => this.setState({ companies: results, companiesLoading: false }),
        (error) =>  this.handleAPIError(error, { companiesLoading: false})
      )
    )
  }

  componentWillUnmount() {
    this.subscriptions && this.subscriptions.unsubscribe()
    this.exportSubscription && this.exportSubscription.unsubscribe()
    this.loadImageSubscription && this.loadImageSubscription.unsubscribe()
    this.loadReportSubscription && this.loadReportSubscription.unsubscribe()
  }

  componentDidUpdate(prevProps: PositiveReportProps, prevState: PositiveReportState) {
    if (this.state.pageNumber !== prevState.pageNumber) {
      this.refreshGrid(this.state.pageNumber)
    }
  }

  refreshGrid(pageNumber: number) {
    this.setState({ isLoading: true })
    this.loadReportSubscription && this.loadReportSubscription.unsubscribe()
    const companyIds: number[] = this.state.selectedCompanies?.map(a => a.AccountID) || []

    this.loadReportSubscription = PositiveReportLoad({
      DateFrom: FormatDate(this.state.from),
      DateTo: FormatDate(this.state.to),
      DateType: this.state.selectedRange,
      PageNumber: pageNumber,
      CompanyIds: companyIds,
      PageSize: PageSize
    }).subscribe(
      data => this.setState({ data: data, isLoading: false }),
      error => this.handleAPIError(error, { isLoading: false})
    )
  }

  handleDateRangeChange = (event: React.ChangeEvent<HTMLSelectElement>) => this.setState({ selectedRange: event.target.value })

  onCompanyChange = (selected: AutoFilterCompany[]) => {
    this.setState({ selectedCompanies: selected || [] })
  }

  pageChanged = (pageNumber: number) => {
    this.setState({ pageNumber: pageNumber })
  }

  handleClearClick = () => {
    this.setState({
      selectedCompanies: [],
      from: undefined,
      to: undefined,
      selectedRange: this.state.ranges[0]
     })
  }

  export(csv: string) {
    const csvData = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    FileSaver.saveAs(csvData, FormatTimestampFileName("Positive_report", "csv", new Date()), true)
    toast.success("CSV file downloaded successfully.")
  }

  printMROReport = (row: PositiveReportRow) => {
    this.loadReportSubscription && this.loadReportSubscription.unsubscribe()
    this.loadReportSubscription = GenerateMROReport(row.TestDetailID).subscribe(
      (data) => this.processReport(data),
      (error) => this.handleAPIError(error, {})
    )
  }

  processReport = (data: string) => {
    if (data && data !== "") {
      printPdf(data)
    } else {
      toast.error("Data not found.")
    }
  }

  handleLoadClick = () => {
    this.refreshGrid(1)
  }

  handleExportClick = () => {
    this.setState({ loadingCSV: true })
    const companyIds: number[] = this.state.selectedCompanies?.map(a => a.AccountID) || []
    PositiveReportExport({
      DateFrom: FormatDate(this.state.from),
      DateTo: FormatDate(this.state.to),
      DateType: this.state.selectedRange,
      CompanyIds: companyIds,
    }).subscribe(
      (data: string) => {
        this.export(data)
        this.setState({ loadingCSV: false })
      } ,
      error => this.handleAPIError(error, {loadingCSV: false})
    )
  }

  isReportParametersValid = () => {
    const { from, to, selectedCompanies } = this.state
    return (!!from && !!to && selectedCompanies.length > 0)
  }

  handleAPIError = (error: Error | APIError, stateChanges: object) => {
    this.setState(stateChanges)
    error && toast.error((error as APIError)?.errorData?.message || error.message)
  }

  mapMROReport = (row: PositiveReportRow) => {
    return <img onClick={() => this.printMROReport(row)} src={ImageMRO} alt={"MRO Report"} title={"MRO Report"} />
  }

  renderRow = (row: PositiveReportRow) => {
    return <>
      <td>{row.CompanyName}</td>
      <td>{row.LocationName}</td>
      <td>{row.RegulatoryMode}</td>
      <td>{row.EmployeeCategory}</td>
      <td>{row.ReferenceID}</td>
      <td>{row.DonorID}</td>
      <td>{row.DonorFirstName}</td>
      <td>{row.DonorLastName}</td>
      <td>{row.ResultDescription}</td>
      <td>{row.TestType}</td>
      <td className="align-text-top">{FormatDate(row.DateCollected)}</td>
      <td className="align-text-top">{FormatDate(row.MROReportDate)}</td>
      <td>{row.PositiveDrugList}</td>
      <td>{this.mapMROReport(row) }</td>
    </>
  }

  render() {
    const { isLoading, data, pageNumber, from, to, selectedRange, companiesLoading, loadingCSV } = this.state
    const emptyRow = <tr><td colSpan={13}>NO DATA FOUND</td></tr>
    const dataRows = (rows: PositiveReportRow[]) => rows.map((row: PositiveReportRow, i: number) =>
      <tr key={i}>
        {this.renderRow(row)}
      </tr>)

    return <AppPageWrapper showNav={true}>
      <Modal.Dialog size="xl">
        <Modal.Header>
          <Modal.Title className="text-uppercase">POSITIVE ONLY REPORT</Modal.Title>
        </Modal.Header>
        <Modal.Body>
        <Form.Row>
          <Col lg={6}><Form.Label column>Companies Filter:</Form.Label></Col>
          <Col lg={{span: 5, offset: 1}}><Form.Label className="pl-1" column>Date Range Filter:</Form.Label></Col>
        </Form.Row>
        <Form.Row className="ml-2">
          <Col lg={6}>{!companiesLoading && <DualListBox
              disabled={false}
              options={this.state.companies || []}
              selected={this.state.selectedCompanies || []}
              idKey="AccountID"
              labelKey="Name"
              availableTitle="Available"
              selectedTitle="Selected"
              onChange={this.onCompanyChange}
            />}</Col>
          <Col lg={{span: 5, offset: 1}} className="mt-3">
            <Form.Row className="form-group">
              <Col lg={8}>
                <Form.Control id="date-range" as="select" onChange={this.handleDateRangeChange} value={selectedRange || ""}>
                  {this.state.ranges?.map(r => <option value={r} key={r} selected={r === selectedRange}>{r}</option>)}
                </Form.Control>
              </Col>
            </Form.Row>
            <Form.Row className="form-group">
              <Col>
                <FromToDates from={from} to={to} onChangeFrom={(date: Date) => {this.setState({from: date})}} onChangeTo={(date: Date) => {this.setState({to: date})}}/>
              </Col>
            </Form.Row>
          </Col>
        </Form.Row>
        </Modal.Body>
        <Modal.Footer>
          <ActionButton size="sm" className="mr-auto" onClick={() => this.handleLoadClick()} disabled={!this.isReportParametersValid()} spin={this.state.isLoading} disableOnSpin>LOAD</ActionButton>
          <Button variant="secondary" size="sm" className="mr-1" onClick={() => this.handleExportClick()} disabled={!this.isReportParametersValid()}>
            EXPORT CSV&nbsp;
            {loadingCSV ? <Spinner animation="border" size="sm" /> : <img src={downloadCsv} alt="" width="16" height="16" />}
          </Button>
          <Button variant="secondary" size="sm" className="mr-1" onClick={() => this.handleClearClick()}>CLEAR FILTERS&nbsp;<FaSync /></Button>
        </Modal.Footer>
      </Modal.Dialog>
      <Container fluid className="grid-container">
          <Col>
            <Table bordered responsive>
              <thead>
                <tr>
                  <td>Company</td>
                  <td>Location</td>
                  <td>Regulatory Mode</td>
                  <td>Employee Category</td>
                  <td>CCF#</td>
                  <td>DonorID</td>
                  <td>Donor First Name</td>
                  <td>Donor Last Name</td>
                  <td>Result</td>
                  <td>Test Type</td>
                  <td>Date Collected</td>
                  <td>Date Reported</td>
                  <td>PositiveDrugList</td>
                  <td></td>
                </tr>
              </thead>
              <tbody>
                {
                  isLoading ?
                    <tr><td colSpan={13}><Spinner animation="border" variant="secondary" /></td></tr> :
                    data?.Records && data.Records.length > 0 ? dataRows(data.Records) : emptyRow
                }
              </tbody>
            </Table>
            {data && !isLoading &&
              <span className="total-records">Total records: {data.Total}</span>}
            {data && !isLoading &&
              <TablePagination pageNumber={pageNumber} pages={data.Pages} onChange={this.pageChanged} />}
          </Col>
          </Container>
    </AppPageWrapper>
  }
}